import { styled } from '@linaria/react';
import { fonts, radius, semantic, size, space } from '../../theme/tokens';
import { EntityImage } from '../../components/EntityImage';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: var(--layout-viewport-height, 100vh);
  max-height: var(--layout-viewport-height, 100vh);
  overflow: hidden;
  background: ${semantic.bg.canvas};
  font-family: ${fonts.body};
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${space['space-400']} ${space['space-600']};
`;

export const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${space['space-1000']};
  height: ${space['space-1000']};
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${semantic.text.primary};
  padding: 0;
  border-radius: ${radius('button', 'radius-full')};

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-400']};
  padding: 0 ${space['space-600']};
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

export const Section = styled.div<{ $grow?: boolean }>`
  border: ${size.stroke['stroke-default']} solid ${semantic.border.subtle};
  border-radius: ${radius('card', 'radius-md')};
  background: ${semantic.bg.canvas};
  display: ${({ $grow }) => ($grow ? 'flex' : 'block')};
  flex-direction: column;
  flex: ${({ $grow }) => ($grow ? '1 1 auto' : '0 0 auto')};
  min-height: ${({ $grow }) => ($grow ? '0' : 'auto')};
  overflow: ${({ $grow }) => ($grow ? 'hidden' : 'visible')};
`;

export const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  padding: ${space['space-600']};
  cursor: pointer;
  font-family: ${fonts.body};

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
    border-radius: ${radius('card', 'radius-md')};
  }
`;

export const SectionLabel = styled.span`
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.bold};
  color: ${semantic.text.primary};
`;

export const SectionValue = styled.span<{ $placeholder?: boolean }>`
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  color: ${(p) => (p.$placeholder ? semantic.text.secondary : semantic.text.primary)};
`;

export const SectionBody = styled.div<{ $grow?: boolean; $noPadding?: boolean }>`
  padding: ${({ $noPadding }) =>
    $noPadding ? '0' : `0 ${space['space-600']} ${space['space-600']}`};
  display: ${({ $grow }) => ($grow ? 'flex' : 'block')};
  flex-direction: column;
  flex: ${({ $grow }) => ($grow ? '1 1 auto' : '0 0 auto')};
  min-height: ${({ $grow }) => ($grow ? '0' : 'auto')};
  overflow: ${({ $grow }) => ($grow ? 'hidden' : 'visible')};
`;

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-400']};
`;

export const CategoryOption = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space['space-400']};
  width: 100%;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${(p) => (p.$active ? fonts.weight.semiBold : fonts.weight.medium)};
  color: ${semantic.text.primary};
  text-align: left;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
    border-radius: ${radius('menuItem', 'radius-sm')};
  }
`;

export const CategoryThumb = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${space['space-1200']};
  height: ${space['space-1200']};
  border-radius: ${radius('image', 'radius-sm')};
  background: ${semantic.bg.surfaceSubtle};
  overflow: hidden;
`;

export const CategoryThumbImg = styled(EntityImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${space['space-600']};
  border-top: ${size.stroke['stroke-default']} solid ${semantic.border.subtle};
`;

export const ResetButton = styled.button`
  background: none;
  border: none;
  padding: ${space['space-200']} 0;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};

  &:hover {
    font-weight: ${fonts.weight.semiBold};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &:disabled {
    color: ${semantic.text.tertiary};
    cursor: not-allowed;
  }
`;

export const NextButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${space['space-300']} ${space['space-600']};
  background: ${semantic.bg.brand};
  color: ${semantic.text.onBrand};
  border: none;
  border-radius: ${radius('button', 'radius-full')};
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;
